import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import { Brain, Heart, Shield, Activity, Sparkles, HelpCircle, ArrowRight } from 'lucide-react';
import FluidSubpageHero from '../components/FluidSubpageHero';
import { localizePath } from '../lib/routeMapping';

export default function PsykiatriskeLidelser() {
  const { lang, getLocalizedValue } = useLanguage();
  const [cmsData, setCmsData] = useState(null);

  useEffect(() => {
    const fetchCmsData = async () => {
      try {
        const res = await fetch('/_api/public/content/psykiatriske_lidelser_side');
        if (res.ok) {
          const items = await res.json();
          if (Array.isArray(items) && items.length > 0) {
            setCmsData(items[0]);
          }
        }
      } catch (err) {
        console.error('Error fetching psykiatriske_lidelser_side CMS data:', err);
      }
    };
    fetchCmsData();
  }, []);

  const seoTitle = lang === 'da' 
    ? 'Psykiatriske Lidelser – Udredning & Behandling | Nordisk Psykiatri' 
    : 'Psykiatriska Tillstånd – Utredning & Behandling | Nordisk Psykiatri';

  const seoDesc = lang === 'da'
    ? 'Få specialiseret udredning og behandling for angst, depression, OCD, PTSD og personlighedsforstyrrelser hos Nordisk Psykiatri i København.'
    : 'Få specialiserad utredning och behandling för ångest, depression, OCD, PTSD och personlighetssyndrom hos Nordisk Psykiatri i Köpenhamn.';

  useDocumentMetadata(seoTitle, seoDesc, { lang });

  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [cmsFaqItems, setCmsFaqItems] = useState([]);

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  useEffect(() => {
    const fetchFaq = async () => {
      try {
        const res = await fetch('/_api/public/v2/content/ofte_stillede_spoergsmaal?term=faq-kategori:psykiatriske-lidelser&sort=sortering&order=asc&limit=20');
        if (res.ok) {
          const data = await res.json();
          if (data && Array.isArray(data.items) && data.items.length > 0) {
            setCmsFaqItems(data.items);
          }
        }
      } catch (err) {
        console.error('Error fetching FAQ items:', err);
      }
    };
    fetchFaq();
  }, []);

  // Localized texts with lorem ipsum placeholders
  const content = {
    da: {
      hero: {
        eyebrow: 'PSYKIATRISKE LIDELSER',
        title: 'Udredning og behandling af psykiatriske lidelser',
        desc: 'Vi tilbyder udredning og behandling af en bred vifte af psykiatriske lidelser. Forløbene tilpasses dine behov og bygger på evidensbaserede metoder, høj faglighed og et tæt samarbejde.',
        cta: 'Book indledende samtale'
      },
      intro: {
        title: 'Forståelse og professionel støtte',
        text1: 'Mange mennesker oplever i løbet af livet psykiatriske udfordringer eller tilstande, der påvirker livskvaliteten, relationerne og hverdagens overskud. Hos Nordisk Psykiatri tilbyder vi grundig udredning og skræddersyet behandling i trygge, diskrete rammer.',
        text2: 'Vores forløb tilrettelægges af erfarne speciallæger i psykiatri og kliniske psykologer, der arbejder tæt sammen for at give dig den mest præcise diagnose og en effektiv behandlingsplan.',
      },
      disorders: {
        title: 'Vi har særligt fokus på',
        subtitle: 'Klik på en lidelse for at læse mere om symptomer, udredning og behandling.',
        items: [
          {
            icon: <Brain className="w-6 h-6 text-petrol-dark" />,
            title: 'Angstlidelser',
            desc: 'Angst kan komme til udtryk på mange måder og påvirke både krop, tanker og adfærd. Vi behandler blandt andet generaliseret angst, social angst, panikangst, agorafobi, specifikke fobier og helbredsangst.',
            link: '/psykiatriske-lidelser/angst',
            linkText: 'Læs mere'
          },
          {
            icon: <Sparkles className="w-6 h-6 text-petrol-dark" />,
            title: 'OCD',
            desc: 'OCD er kendetegnet ved tilbagevendende tvangstanker og tvangshandlinger, som kan være tidskrævende og belastende. Vi tilbyder effektiv behandling med særligt fokus på eksponering med responshindring (ERP) og kognitiv adfærdsterapi.',
            link: '/psykiatriske-lidelser/ocd',
            linkText: 'Læs mere'
          },
          {
            icon: <Heart className="w-6 h-6 text-petrol-dark" />,
            title: 'Depression',
            desc: 'Depression kan påvirke humør, energi, koncentration og livskvalitet. Vi tilbyder grundig udredning og behandling baseret på den nyeste viden og nationale anbefalinger.',
            link: '/psykiatriske-lidelser/depression',
            linkText: 'Læs mere'
          },
          {
            icon: <Shield className="w-6 h-6 text-petrol-dark" />,
            title: 'PTSD og belastningsreaktioner',
            desc: 'Traumatiske oplevelser kan føre to vedvarende symptomer som flashbacks, undgåelse og øget alarmberedskab. Vi tilbyder evidensbaseret behandling med fokus på tryghed og gradvis bearbejdning.',
            link: '/psykiatriske-lidelser/ptsd',
            linkText: 'Læs mere'
          },
          {
            icon: <Activity className="w-6 h-6 text-petrol-dark" />,
            title: 'Personlighedsforstyrrelser',
            desc: 'Personlighedsforstyrrelser påvirker måden, man oplever sig selv og indgår i relationer på. Vi tilbyder grundig diagnostik og målrettede terapiforløb med fokus på forandring og trivsel.',
            link: '/psykiatriske-lidelser/personlighedsforstyrrelser',
            linkText: 'Læs mere'
          },
          {
            icon: <HelpCircle className="w-6 h-6 text-petrol-dark" />,
            title: 'Andre lidelser',
            desc: 'Vi tilbyder også udredning og behandling ved blandt andet bipolar lidelse, spiseforstyrrelser, søvnproblemer og stressrelaterede reaktioner.',
            link: '/vidensbank?category=Behandling',
            linkText: 'Læs mere'
          }
        ]
      },
      faq: {
        eyebrow: 'FAQ',
        title: 'Ofte stillede spørgsmål om psykiatriske lidelser',
        items: [
          {
            q: 'Kan man have både angst og depression på samme tid?',
            a: 'Ja, det er meget almindeligt. Langvarig angst kan føre til nedtrykthed, og depression kan gøre en uro værre. Ved udredningen ser vi på begge dele, så behandlingen rammer det samlede billede.'
          },
          {
            q: 'Går en angstlidelse over af sig selv?',
            a: 'Angstsymptomer kan variere over tid, men hvis de påvirker hverdagen, er det en god idé at søge professionel hjælp.'
          },
          {
            q: 'Hvornår er tristhed en depression?',
            a: 'Hvis tristhed eller manglende interesse varer ved og påvirker din funktion i hverdagen, kan det være relevant at blive vurderet af en fagperson.'
          },
          {
            q: 'Hvad er forskellen på OCD og almindelig bekymring?',
            a: 'OCD er typisk kendetegnet ved påtrængende tanker og gentagne handlinger eller mentale ritualer, som kan være tidskrævende og belastende.'
          },
          {
            q: 'Kan ADHD eller autisme ligge bag angst og depression?',
            a: 'Ja, de kan forekomme samtidig. En grundig udredning kan hjælpe med at forstå sammenhængene.'
          },
          {
            q: 'Hvornår giver det mening med medicin?',
            a: 'Det afhænger af symptomer, diagnose, tidligere behandling og dine ønsker. En læge kan vurdere mulighederne sammen med dig.'
          }
        ]
      },
      seekHelp: {
        eyebrow: 'Tegn at være opmærksom på',
        title: 'Hvornår bør du søge hjælp?',
        desc: 'Alle oplever perioder med bekymring, tristhed eller uro. Det bliver relevant at søge hjælp, når det ikke går over af sig selv og begynder at styre din hverdag.',
        symptoms: [
          'Symptomerne har stået på i flere uger og bliver ikke bedre.',
          'Du begynder at undgå steder, mennesker eller opgaver for at holde ubehaget væk.',
          'Søvn, koncentration eller energi er så påvirket, at det går ud over arbejde, studie eller relationer.',
          'Tanker eller ritualer tager mere og mere tid, selvom du godt ved, de er overdrevne.',
          'Du har prøvet at håndtere det selv – eller fået hjælp – uden at det har rykket nok.'
        ]
      },
      holistic: {
        eyebrow: 'Det samlede billede',
        title: 'Når flere ting hænger sammen',
        p1: 'Psykiske vanskeligheder kommer sjældent alene. Angst og depression optræder ofte samtidig, og langvarig stress, søvnproblemer eller traumer kan forstærke begge dele.',
        p2: 'Hos voksne ser vi også, at en ikke-opdaget ADHD eller autisme kan ligge bag år med angst, nedtrykthed eller udmattelse. Behandles kun det, der er mest synligt, kommer man ofte ikke helt i mål.',
        p3: 'Derfor ser vi altid på det samlede billede. Psykolog og læge vurderer sammen, hvad der er hvad, så behandlingen rammer det, der faktisk driver dine vanskeligheder.',
        linkText: 'Læs: ADHD, angst og depression hos voksne →',
        linkUrl: '/vidensbank/adhd-angst-og-depression-naar-det-hele-haenger-sammen'
      },
      methods: {
        eyebrow: 'Evidensbaserede metoder',
        title: 'Sådan behandler vi',
        desc: 'Vi vælger metode ud fra din diagnose og din situation – og kombinerer psykologisk og medicinsk behandling, når det giver mening.',
        items: [
          {
            badge: 'Angst · Depression',
            title: 'Psykodynamisk terapi',
            text: 'Du arbejder med at forstå, hvordan tidligere erfaringer og ubevidste mønstre præger dine følelser og relationer i dag – så du får mulighed for at ændre dem.'
          },
          {
            badge: 'OCD',
            title: 'Eksponering med responshindring',
            text: 'Gradvis og planlagt konfrontation med det, der udløser tvangen – uden at udføre ritualet.'
          },
          {
            badge: 'PTSD · Belastning',
            title: 'Traumefokuseret behandling',
            text: 'Tryg og gradvis bearbejdning af det, der er sket, så minderne mister deres greb om hverdagen.'
          },
          {
            badge: 'Personlighed',
            title: 'MBT og DAT',
            text: 'Strukturerede terapiformer, der styrker forståelsen af dig selv og andre og evnen til at regulere følelser.'
          },
          {
            badge: 'Alle forløb',
            title: 'Psykoedukation',
            text: 'Viden om din diagnose og om, hvad der hjælper – så du selv kan tage del i behandlingen.'
          },
          {
            badge: 'Ved behov',
            title: 'Medicinsk behandling',
            text: 'Vurdering, opstart og tæt opfølgning ved læge, med fokus på effekt og bivirkninger.'
          }
        ]
      },
      articles: {
        eyebrow: 'Vidensbank',
        title: 'Bliv klogere, før du booker',
        linkText: 'Alle artikler →',
        items: [
          {
            category: 'Udredning',
            title: 'Hvordan foregår en psykiatrisk udredning?',
            slug: '/vidensbank/hvordan-foregaar-psykiatrisk-udredning',
            image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/chatgpt-image-sep-9-2026-10-10-54-am.png',
            bg: '#e8dfc8'
          },
          {
            category: 'Guide',
            title: 'Forskel på psykolog og psykiater',
            slug: '/vidensbank/psykolog-eller-psykiater-hvad-er-forskellen',
            image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/psykolog-psykiater.jpg',
            bg: '#e6e6d7'
          },
          {
            category: 'Angst og depression',
            title: 'ADHD, angst og depression hos voksne',
            slug: '/vidensbank/adhd-angst-og-depression-naar-det-hele-haenger-sammen',
            image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/adhd-angst-depression.jpg',
            bg: '#ece8dc'
          }
        ]
      },
      ctaSection: {
        title: 'Er du klar til at tage det næste skridt?',
        desc: 'Book en indledende samtale, så finder vi sammen ud af, hvilket forløb der passer til dig. Du behøver ikke en henvisning.',
        btn: 'Book indledende samtale →',
        address: 'Vesterbrogade 37, 1. tv · 1620 København V'
      },
      notice: {
        label: 'Akut hjælp:',
        text: 'Er du i akut krise eller har selvmordstanker, så ring 112 – eller 1813 (Akuttelefonen) i Region Hovedstaden. Vi er ikke et akuttilbud.'
      }
    },
    sv: {
      hero: {
        eyebrow: 'PSYKIATRISKA TILLSTÅND · VUXNA FRÅN 18 ÅR · INGEN REMISS',
        title: 'Privat utredning och behandling av psykiatriska tillstånd',
        desc: 'Vi erbjuder utredning och behandling av ett brett spektrum av psykiatriska tillstånd. Behandlingen anpassas efter dina behov och bygger på evidensbaserade metoder, hög professionalitet och nära samarbete.',
        cta: 'Boka inledande samtal'
      },
      intro: {
        title: 'Förståelse och professionellt stöd',
        text1: 'Många människor upplever under livet psykiatriska utmaningar eller tillstånd som påverkar livskvaliteten, relationer och ork i vardagen. Hos Nordisk Psykiatri erbjuder vi grundlig utredning och skräddarsydd behandling i trygga, diskreta ramar.',
        text2: 'Våra insatser planeras av erfarna specialistläkare i psykiatri och legitimerade psykologer som arbetar tätt tillsammans för att ge dig en precis bedömning och en effektiv behandlingsplan.',
      },
      disorders: {
        title: 'Vi har särskilt fokus på',
        subtitle: 'Klicka på ett tillstånd för att läsa mer om symptom, utredning och behandling.',
        items: [
          {
            icon: <Brain className="w-6 h-6 text-petrol-dark" />,
            title: 'Ångestsyndrom',
            desc: 'Ångest kan ta sig uttryck på många sätt och påverka både kropp, tankar och beteende. Vi behandlar bland annat generaliserat ångestsyndrom, social ångest, panikångest, agorafobi, specifika fobier och hälsoångest.',
            link: '/psykiatriske-lidelser/angst',
            linkText: 'Läs mer'
          },
          {
            icon: <Sparkles className="w-6 h-6 text-petrol-dark" />,
            title: 'OCD',
            desc: 'OCD kännetecknas av återkommande tvångstankar och tvångshandlingar, som kan vara tidskrävande och betungande. Vi erbjuder effektiv behandling med särskilt fokus på exponering med responsprevention (ERP) och kognitiv beteendeterapi.',
            link: '/psykiatriske-lidelser/ocd',
            linkText: 'Läs mer'
          },
          {
            icon: <Heart className="w-6 h-6 text-petrol-dark" />,
            title: 'Depression',
            desc: 'Depression kan påverka humör, energi, koncentration och livskvalitet. Vi erbjuder grundlig utredning och behandling baserad på den senaste kunskapen och nationella rekommendationer.',
            link: '/psykiatriske-lidelser/depression',
            linkText: 'Läs mer'
          },
          {
            icon: <Shield className="w-6 h-6 text-petrol-dark" />,
            title: 'PTSD och belastningsreaktioner',
            desc: 'Traumatiska upplevelser kan leda till ihållande symptom som flashbacks, undvikande och ökad vaksamhet. Vi erbjuder evidensbaserad behandling med fokus på trygghet och gradvis bearbetning.',
            link: '/psykiatriske-lidelser/ptsd',
            linkText: 'Läs mer'
          },
          {
            icon: <Activity className="w-6 h-6 text-petrol-dark" />,
            title: 'Personlighetsstörningar',
            desc: 'Personlighetsstörningar påverkar hur man upplever sig själv och fungerar i relationer. Vi erbjuder grundlig diagnostik och riktade terapiförlopp med fokus på förändring och välbefinnande.',
            link: '/psykiatriske-lidelser/personlighedsforstyrrelser',
            linkText: 'Läs mer'
          },
          {
            icon: <HelpCircle className="w-6 h-6 text-petrol-dark" />,
            title: 'Andra tillstånd',
            desc: 'Vi erbjuder också utredning och behandling vid bland annat bipolär sjukdom, ätstörningar, sömnproblem och stressrelaterade reaktioner.',
            link: '/kontakt',
            linkText: 'Läs mer'
          }
        ]
      },
      faq: {
        eyebrow: 'FAQ',
        title: 'Vanliga frågor om psykiatriska tillstånd',
        items: [
          {
            q: 'Kan man ha både ångest och depression samtidigt?',
            a: 'Ja, det är mycket vanligt. Långvarig ångest kan leda till nedstämdhet, och depression kan förvärra oro. Vid utredningen ser vi på båda delarna så behandlingen adresserar hela bilden.'
          },
          {
            q: 'Går ett ångestsyndrom över av sig självt?',
            a: 'Ångestsymptom kan variera över tid, men om de påverkar vardagen och funktionsnivån är det en god idé att söka professionell hjälp.'
          },
          {
            q: 'När är nedstämdhet en depression?',
            a: 'Om nedstämdhet eller minskat intresse kvarstår i flera veckor och påverkar vardagen, kan det vara relevant att få en bedömning av en specialist.'
          },
          {
            q: 'Vad är skillnaden mellan OCD och vanlig oro?',
            a: 'OCD kännetecknas vanligen av påträngande tvångstankar och upprepade handlingar eller mentala ritualer som tar tid och orsakar lidande.'
          },
          {
            q: 'Kan ADHD eller autism ligga bakom ångest och depression?',
            a: 'Ja, de förekommer ofta samtidigt. En grundlig utredning hjälper till att förstå orsakssambanden och säkerställa rätt stöd.'
          },
          {
            q: 'När är medicinsk behandling lämplig?',
            a: 'Det beror på symptom, diagnos, tidigare behandlingssvar och dina egna önskemål. En läkare utvärderar möjligheterna tillsammans med dig.'
          }
        ]
      },
      seekHelp: {
        eyebrow: 'TECKEN ATT VARA UPPMÄRKSAM PÅ',
        title: 'När bör du söka hjälp?',
        desc: 'Alla upplever perioder av oro, nedstämdhet eller rastlöshet. Att söka hjälp blir relevant när det inte går över av sig självt och börjar styra din vardag.',
        symptoms: [
          'Symptomen har pågått i flera veckor och blir inte bättre.',
          'Du börjar undvika platser, människor eller uppgifter för att hålla obehaget borta.',
          'Sömn, koncentration eller ork är så påverkad att det går ut över arbete, studier eller relationer.',
          'Tankar eller ritualer tar alltmer tid, även om du vet att de är överdrivna.',
          'Du har försökt hantera det själv – eller fått hjälp – utan att det har hjälpt tillräckligt.'
        ]
      },
      holistic: {
        eyebrow: 'HELA BILDEN',
        title: 'När flera saker hänger ihop',
        p1: 'Psykiska svårigheter kommer sällan ensamma. Ångest och depression uppträder ofta samtidigt, och långvarig stress, sömnproblem eller trauman kan förstärka båda delarna.',
        p2: 'Hos vuxna ser vi också att en oupptäckt ADHD eller autism kan ligga bakom år av ångest, nedstämdhet eller utmattning. Behandlar man bara det mest synliga kommer man ofta inte hela vägen.',
        p3: 'Därför ser vi alltid till helhetsbilden. Psykolog och läkare bedömer tillsammans vad som är vad, så behandlingen träffar det som faktiskt driver dina svårigheter.',
        linkText: 'Läs: ADHD, ångest och depression hos vuxna →',
        linkUrl: '/vidensbank/adhd-angst-og-depression-naar-det-hele-haenger-sammen'
      },
      methods: {
        eyebrow: 'EVIDENSBASERADE METODER',
        title: 'Så behandlar vi',
        desc: 'Vi väljer metod utifrån din diagnos och din situation – och kombinerar psykologisk och medicinsk behandling när det är meningsfullt.',
        items: [
          {
            badge: 'Ångest · Depression',
            title: 'Psykodynamisk terapi',
            text: 'Du arbetar med att förstå hur tidigare erfarenheter och omedvetna mönster präglar dina känslor och relationer idag – så du får möjlighet att förändra dem.'
          },
          {
            badge: 'OCD',
            title: 'Exponering med responsprevention',
            text: 'Gradvis och planerad konfrontation med det som utlöser tvånget – utan att utföra ritualen.'
          },
          {
            badge: 'PTSD · Belastning',
            title: 'Traumafokuserad behandling',
            text: 'Trygg och gradvis bearbetning av det som hänt, så minnena tappar sitt grepp om vardagen.'
          },
          {
            badge: 'Personlighet',
            title: 'MBT och DBT',
            text: 'Strukturerade terapiformer som stärker förståelsen av dig själv och andra och förmågan att reglera känslor.'
          },
          {
            badge: 'Alla förlopp',
            title: 'Psykoedukation',
            text: 'Kunskap om din diagnos och vad som hjälper – så du själv kan ta aktiv del i behandlingen.'
          },
          {
            badge: 'Vid behov',
            title: 'Medicinsk behandling',
            text: 'Bedömning, insättning och tät uppföljning av läkare, med fokus på effekt och biverkningar.'
          }
        ]
      },
      articles: {
        eyebrow: 'KUNSKAPSBANK',
        title: 'Läs mer innan du bokar',
        linkText: 'Alla artiklar →',
        items: [
          {
            category: 'Utredning',
            title: 'Hur går en psykiatrisk utredning till?',
            slug: '/vidensbank/hvordan-foregaar-psykiatrisk-udredning',
            image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/chatgpt-image-sep-9-2026-10-10-54-am.png',
            bg: '#e8dfc8'
          },
          {
            category: 'Guide',
            title: 'Skillnad mellan psykolog och psykiater',
            slug: '/vidensbank/psykolog-eller-psykiater-hvad-er-forskellen',
            image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/psykolog-psykiater.jpg',
            bg: '#e6e6d7'
          },
          {
            category: 'Ångest och depression',
            title: 'ADHD, ångest och depression hos vuxna',
            slug: '/vidensbank/adhd-angst-og-depression-naar-det-hele-haenger-sammen',
            image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/adhd-angst-depression.jpg',
            bg: '#ece8dc'
          }
        ]
      },
      ctaSection: {
        title: 'Är du redo att ta nästa steg?',
        desc: 'Boka ett inledande samtal så tar vi tillsammans reda på vilket förlopp som passar dig bäst. Du behöver ingen remiss.',
        btn: 'Boka inledande samtal →',
        address: 'Vesterbrogade 37, 1. tv · 1620 Köpenhamn V'
      },
      notice: {
        label: 'Akut hjälp:',
        text: 'Är du i akut kris eller har självmordstankar, ring 112 – eller 1813 i Region Hovedstaden. Vi är inte en akutmottagning.'
      }
    },
    en: {
      hero: {
        eyebrow: 'PSYCHIATRIC CONDITIONS · ADULTS AGED 18+ · NO REFERRAL',
        title: 'Private assessment and treatment of psychiatric conditions',
        desc: 'We offer assessment and evidence-based treatment for a wide range of psychiatric conditions. Care pathways are tailored to your needs through close psychologist and psychiatrist collaboration.',
        cta: 'Book initial consultation'
      },
      intro: {
        title: 'Understanding and professional support',
        text1: 'Many people experience mental health challenges that affect quality of life, relationships, and everyday capacity. At Nordisk Psykiatri in Copenhagen, we offer thorough assessment and personalized treatment in a safe, confidential clinic setting.',
        text2: 'Our pathways are conducted by experienced consultant psychiatrists and clinical psychologists working closely together to provide an accurate diagnosis and an effective treatment plan.',
      },
      disorders: {
        title: 'Our particular focus',
        subtitle: 'Click on a condition to read more about symptoms, assessment, and treatment.',
        items: [
          {
            icon: <Brain className="w-6 h-6 text-petrol-dark" />,
            title: 'Anxiety disorders',
            desc: 'Anxiety can affect body, thoughts, and behaviour. We treat generalised anxiety, social anxiety, panic disorder, agoraphobia, specific phobias, and health anxiety.',
            link: '/psykiatriske-lidelser/angst',
            linkText: 'Read more'
          },
          {
            icon: <Sparkles className="w-6 h-6 text-petrol-dark" />,
            title: 'OCD',
            desc: 'OCD is characterized by recurrent obsessive thoughts and compulsive actions that can be time-consuming and distressing. We offer effective treatment focusing on ERP and CBT.',
            link: '/psykiatriske-lidelser/ocd',
            linkText: 'Read more'
          },
          {
            icon: <Heart className="w-6 h-6 text-petrol-dark" />,
            title: 'Depression',
            desc: 'Depression can affect mood, energy, concentration, and quality of life. We offer thorough assessment and treatment based on current clinical guidelines.',
            link: '/psykiatriske-lidelser/depression',
            linkText: 'Read more'
          },
          {
            icon: <Shield className="w-6 h-6 text-petrol-dark" />,
            title: 'PTSD and stress reactions',
            desc: 'Traumatic experiences can lead to persistent symptoms such as flashbacks, avoidance, and hyperarousal. We provide evidence-based treatment tailored to your safety and pace.',
            link: '/psykiatriske-lidelser/ptsd',
            linkText: 'Read more'
          },
          {
            icon: <Activity className="w-6 h-6 text-petrol-dark" />,
            title: 'Personality disorders',
            desc: 'Personality disorders influence how one experiences self and relationships. We offer diagnostic assessment and targeted therapy.',
            link: '/psykiatriske-lidelser/personlighedsforstyrrelser',
            linkText: 'Read more'
          },
          {
            icon: <HelpCircle className="w-6 h-6 text-petrol-dark" />,
            title: 'Other conditions',
            desc: 'We also offer assessment and treatment for other psychiatric conditions, sleep difficulties, and stress-related reactions.',
            link: '/kontakt',
            linkText: 'Contact us'
          }
        ]
      },
      faq: {
        eyebrow: 'FAQ',
        title: 'Frequently asked questions about psychiatric conditions',
        items: [
          {
            q: 'Can you have both anxiety and depression at the same time?',
            a: 'Yes, that is very common. Prolonged anxiety can lead to low mood, and depression can worsen restlessness and worry. During assessment, we look at both so treatment addresses the complete clinical picture.'
          },
          {
            q: 'Does an anxiety disorder go away on its own?',
            a: 'Anxiety symptoms can fluctuate over time, but if they impact your daily life and functioning, seeking professional support is advisable.'
          },
          {
            q: 'When does sadness become a clinical depression?',
            a: 'If low mood, loss of interest, or exhaustion persists for weeks and impairs daily function, a professional psychiatric evaluation is recommended.'
          },
          {
            q: 'What is the difference between OCD and ordinary worry?',
            a: 'OCD is typically characterized by intrusive, repetitive thoughts and compulsive actions or mental rituals that consume significant time and cause distress.'
          },
          {
            q: 'Can ADHD or autism underlie anxiety and depression?',
            a: 'Yes, they often co-occur. A thorough psychiatric evaluation helps clarify the underlying root causes and connections.'
          },
          {
            q: 'When is medication appropriate?',
            a: 'It depends on your symptoms, diagnosis, previous treatment responses, and personal preferences. A medical specialist evaluates options collaboratively with you.'
          }
        ]
      },
      seekHelp: {
        eyebrow: 'SIGNS TO BE AWARE OF',
        title: 'When should you seek help?',
        desc: 'Everyone experiences periods of worry, sadness, or restlessness. Seeking help becomes relevant when it does not pass on its own and begins to control your everyday life.',
        symptoms: [
          'Symptoms have persisted for several weeks and are not improving.',
          'You begin avoiding places, people, or tasks to keep discomfort at bay.',
          'Sleep, concentration, or energy is so impaired that it affects work, studies, or relationships.',
          'Thoughts or rituals take up more and more time, even though you know they are excessive.',
          'You have tried managing it yourself – or received help – without sufficient progress.'
        ]
      },
      holistic: {
        eyebrow: 'THE COMPLETE PICTURE',
        title: 'When multiple factors are connected',
        p1: 'Psychological challenges rarely occur in isolation. Anxiety and depression frequently co-occur, and prolonged stress, sleep difficulties, or trauma can amplify both.',
        p2: 'In adults, we often find that undiagnosed ADHD or autism underlies years of anxiety, low mood, or chronic exhaustion. If only the most visible symptom is treated, the core problem remains unresolved.',
        p3: 'Therefore, we always evaluate the complete clinical picture. Our psychologist and doctor assess together to identify root causes, ensuring treatment targets what is actually driving your difficulties.',
        linkText: 'Read: ADHD, anxiety, and depression in adults →',
        linkUrl: '/vidensbank/adhd-angst-og-depression-naar-det-hele-haenger-sammen'
      },
      methods: {
        eyebrow: 'EVIDENCE-BASED METHODS',
        title: 'How we treat',
        desc: 'We select therapeutic methods tailored to your diagnosis and unique situation – combining psychological and medical treatment whenever appropriate.',
        items: [
          {
            badge: 'Anxiety · Depression',
            title: 'Psychodynamic therapy',
            text: 'You work to understand how past experiences and unconscious patterns influence your emotions and relationships today – enabling meaningful change.'
          },
          {
            badge: 'OCD',
            title: 'Exposure and response prevention',
            text: 'Gradual and structured confrontation with obsession triggers – without performing the compulsion.'
          },
          {
            badge: 'PTSD · Stress reactions',
            title: 'Trauma-focused therapy',
            text: 'Safe and structured processing of traumatic events, helping memories lose their grip on everyday life.'
          },
          {
            badge: 'Personality',
            title: 'MBT and DBT',
            text: 'Structured therapy modalities strengthening mentalizing, emotional regulation, and self-understanding.'
          },
          {
            badge: 'All pathways',
            title: 'Psychoeducation',
            text: 'Knowledge about your condition and effective coping strategies – empowering active participation in treatment.'
          },
          {
            badge: 'When indicated',
            title: 'Medical treatment',
            text: 'Evaluation, initiation, and close medical monitoring with emphasis on therapeutic efficacy and tolerability.'
          }
        ]
      },
      articles: {
        eyebrow: 'KNOWLEDGE BASE',
        title: 'Gain insight before you book',
        linkText: 'All articles →',
        items: [
          {
            category: 'Assessment',
            title: 'How does a psychiatric assessment work?',
            slug: '/vidensbank/hvordan-foregaar-psykiatrisk-udredning',
            image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/chatgpt-image-sep-9-2026-10-10-54-am.png',
            bg: '#e8dfc8'
          },
          {
            category: 'Guide',
            title: 'Difference between a psychologist and a psychiatrist',
            slug: '/vidensbank/psykolog-eller-psykiater-hvad-er-forskellen',
            image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/psykolog-psykiater.jpg',
            bg: '#e6e6d7'
          },
          {
            category: 'Anxiety & depression',
            title: 'ADHD, anxiety and depression in adults',
            slug: '/vidensbank/adhd-angst-og-depression-naar-det-hele-haenger-sammen',
            image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/adhd-angst-depression.jpg',
            bg: '#ece8dc'
          }
        ]
      },
      ctaSection: {
        title: 'Are you ready to take the next step?',
        desc: 'Book an initial consultation and we will find the pathway that suits you best. You do not need a referral.',
        btn: 'Book initial consultation →',
        address: 'Vesterbrogade 37, 1. tv · 1620 Copenhagen V'
      },
      notice: {
        label: 'Emergency help:',
        text: 'If you are in acute crisis or experiencing suicidal thoughts, call 112 – or 1813 in Region Hovedstaden. We are not an emergency psychiatric service.'
      }
    }
  };

  const t = content[lang] || (lang === 'en' ? content.en : null) || content.da;

  const methodItems = [1, 2, 3, 4, 5, 6].map((num, i) => {
    const fallback = t.methods?.items?.[i] || {};
    return {
      badge: cmsData?.[`metode_${num}_badge`] ? getLocalizedValue(cmsData[`metode_${num}_badge`]) : fallback.badge,
      title: cmsData?.[`metode_${num}_titel`] ? getLocalizedValue(cmsData[`metode_${num}_titel`]) : fallback.title,
      text: cmsData?.[`metode_${num}_tekst`] ? getLocalizedValue(cmsData[`metode_${num}_tekst`]) : fallback.text,
    };
  });

  const faqList = (cmsFaqItems.length > 0)
    ? cmsFaqItems.map(item => ({
        q: getLocalizedValue(item.spoergsmaal),
        a: getLocalizedValue(item.svar)
      }))
    : (t.faq?.items || []);

  return (
    <div className="bg-[#faf9f5] min-h-screen text-text-dark font-sans">
      {/* Hero Section */}
      <FluidSubpageHero
        className="bg-[#D1E1BE]"
        style={{ backgroundColor: '#D1E1BE' }}
        eyebrow={cmsData?.hero_badge ? getLocalizedValue(cmsData.hero_badge) : t.hero.eyebrow}
        title={cmsData?.hero_overskrift ? getLocalizedValue(cmsData.hero_overskrift) : t.hero.title}
        description={cmsData?.hero_beskrivelse ? getLocalizedValue(cmsData.hero_beskrivelse) : t.hero.desc}
        ctaText={cmsData?.hero_cta_tekst ? getLocalizedValue(cmsData.hero_cta_tekst) : t.hero.cta}
        ctaLink={cmsData?.hero_cta_link || "https://nordisk-psykiatrisk-klinik.planway.com/"}
        imageUrl={cmsData?.hero_billede?.[0] ? `https://assets.auxodev.dk/cdn-cgi/image/width=2400,quality=95,format=auto/${cmsData.hero_billede[0].replace('https://assets.auxodev.dk/', '')}` : undefined}
        imageAlt={cmsData?.hero_billede?.[0] ? "Dekorativt klinikmotiv" : undefined}
      />

      {/* Disorders Grid */}
      <section className="py-16 lg:py-24 bg-sand-medium/20">
        <div className="mx-auto max-w-[1120px] px-6">
          <div className="max-w-3xl text-left mb-16">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-petrol-dark tracking-tight mb-4">
              {cmsData?.lidelser_overskrift ? getLocalizedValue(cmsData.lidelser_overskrift) : t.disorders.title}
            </h2>
            <div className="w-12 h-[3px] bg-[#004f50] rounded-full mb-4" />
            <p className="text-lg text-text-muted">
              {cmsData?.lidelser_undertekst ? getLocalizedValue(cmsData.lidelser_undertekst) : t.disorders.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {t.disorders.items.map((item, index) => (
              <div
                key={index}
                className="bg-sand-light p-8 rounded-2xl border border-sand-medium/60 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col"
              >
                <h3 className="font-display text-3xl font-light tracking-tight text-petrol-dark mb-2">
                  {item.title}
                </h3>
                <div className="w-12 h-[3px] bg-[#004f50] rounded-full mb-4" />
                <p className="text-sm text-text-muted leading-relaxed flex-grow">
                  {item.desc}
                </p>
                {item.link && (
                  <Link
                    to={localizePath(item.link, lang)}
                    className="mt-6 inline-flex items-center text-sm font-semibold text-petrol-medium hover:text-petrol-dark transition-colors group"
                  >
                    <span>{item.linkText}</span>
                    <span className="ml-1 transform group-hover:translate-x-1 transition-transform">→</span>
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 1: Hvornår bør du søge hjælp? (Tegn at være opmærksom på) */}
      <section className="py-20 lg:py-24 bg-[#faf9f5]">
        <div className="mx-auto max-w-[1120px] px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="uppercase text-[11px] font-bold tracking-[0.2em] text-[#82948a] mb-2">
                {cmsData?.soeg_hjaelp_badge ? getLocalizedValue(cmsData.soeg_hjaelp_badge) : t.seekHelp.eyebrow}
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-[42px] font-light text-petrol-dark leading-tight mb-3">
                {cmsData?.soeg_hjaelp_overskrift ? getLocalizedValue(cmsData.soeg_hjaelp_overskrift) : t.seekHelp.title}
              </h2>
              <div className="w-11 h-[2px] bg-[#afc0b5] my-5" />
              {cmsData?.soeg_hjaelp_tekst ? (
                <div
                  className="text-[#53675e] text-[15px] leading-relaxed [&_p]:mb-4"
                  dangerouslySetInnerHTML={{ __html: getLocalizedValue(cmsData.soeg_hjaelp_tekst) }}
                />
              ) : (
                <p className="text-[#53675e] text-[15px] leading-relaxed">
                  {t.seekHelp.desc}
                </p>
              )}
            </div>
            <div className="lg:col-span-7 border-t-2 border-[#aebdb5]">
              {cmsData?.soeg_hjaelp_punkter ? (
                <div
                  className="text-[#53675e] text-[15px] leading-relaxed [&_ul]:list-none [&_ul]:p-0 [&_li]:border-b [&_li]:border-[#dce2db] [&_li]:py-4 [&_li]:flex [&_li]:items-start [&_li]:gap-6 [&_li]:before:content-['—'] [&_li]:before:text-[#afc0b5] [&_li]:before:font-bold"
                  dangerouslySetInnerHTML={{ __html: getLocalizedValue(cmsData.soeg_hjaelp_punkter) }}
                />
              ) : (
                t.seekHelp.symptoms.map((symptom, idx) => (
                  <div key={idx} className="border-b border-[#dce2db] py-4.5 flex items-start gap-6 text-[#53675e] text-[15px] leading-relaxed">
                    <span className="text-[#afc0b5] font-bold select-none text-base leading-snug">—</span>
                    <span>{symptom}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Det samlede billede (Når flere ting hænger sammen - cream background) */}
      <section className="py-20 lg:py-24 bg-[#f5f0e5] border-t border-[#dce2db]">
        <div className="mx-auto max-w-[1120px] px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="uppercase text-[11px] font-bold tracking-[0.2em] text-[#82948a] mb-2">
                {cmsData?.samlet_billede_badge ? getLocalizedValue(cmsData.samlet_billede_badge) : t.holistic.eyebrow}
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-[42px] font-light text-petrol-dark leading-tight">
                {cmsData?.samlet_billede_overskrift ? getLocalizedValue(cmsData.samlet_billede_overskrift) : t.holistic.title}
              </h2>
            </div>
            <div className="lg:col-span-7">
              {cmsData?.samlet_billede_tekst ? (
                <div
                  className="text-[#53675e] text-[15px] leading-relaxed space-y-4 [&_p]:mb-4"
                  dangerouslySetInnerHTML={{ __html: getLocalizedValue(cmsData.samlet_billede_tekst) }}
                />
              ) : (
                <div className="text-[#53675e] text-[15px] leading-relaxed space-y-4">
                  <p>{t.holistic.p1}</p>
                  <p>{t.holistic.p2}</p>
                  <p>{t.holistic.p3}</p>
                </div>
              )}
              <Link
                to={localizePath(cmsData?.samlet_billede_link_url || t.holistic.linkUrl, lang)}
                className="inline-block mt-4 text-petrol-dark font-semibold text-sm underline underline-offset-4 hover:text-petrol-medium transition-colors"
              >
                {cmsData?.samlet_billede_link_tekst ? getLocalizedValue(cmsData.samlet_billede_link_tekst) : t.holistic.linkText}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Evidensbaserede metoder (Sådan behandler vi - de 6 kasser) */}
      <section className="py-20 lg:py-24 bg-[#faf9f5] border-t border-[#dce2db]">
        <div className="mx-auto max-w-[1120px] px-6">
          <div className="max-w-[670px] mb-10">
            <div className="uppercase text-[11px] font-bold tracking-[0.2em] text-[#82948a] mb-2">
              {cmsData?.metoder_badge ? getLocalizedValue(cmsData.metoder_badge) : t.methods.eyebrow}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-[42px] font-light text-petrol-dark leading-tight mb-4">
              {cmsData?.metoder_overskrift ? getLocalizedValue(cmsData.metoder_overskrift) : t.methods.title}
            </h2>
            <p className="text-[#53675e] text-[15px] leading-relaxed">
              {cmsData?.metoder_beskrivelse ? getLocalizedValue(cmsData.metoder_beskrivelse) : t.methods.desc}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t-2 border-[#b6c5bc]">
            {methodItems.map((item, idx) => (
              <div
                key={idx}
                className={`py-7 pr-6 border-b border-[#dce2db] min-h-[190px] flex flex-col justify-start ${
                  (idx + 1) % 3 !== 0 ? 'lg:border-r lg:border-[#dce2db] lg:mr-7' : ''
                } ${idx % 2 === 0 ? 'md:border-r md:border-[#dce2db] md:mr-6 lg:mr-7 lg:border-r' : 'md:border-r-0 md:mr-0'} ${
                  (idx + 1) % 3 === 0 ? 'lg:border-r-0 lg:mr-0' : ''
                }`}
              >
                <div className="uppercase text-[10px] font-bold tracking-[0.2em] text-[#82948a] mb-2">
                  {item.badge}
                </div>
                <h3 className="font-display text-[22px] font-normal text-petrol-dark leading-tight mb-3">
                  {item.title}
                </h3>
                <p className="text-sm text-[#53675e] leading-relaxed flex-grow">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4: FAQ (Ofte stillede spørgsmål - mint #CFE0DB) */}
      <section className="py-20 lg:py-24 bg-[#CFE0DB] border-t border-[#dce2db]">
        <div className="mx-auto max-w-[1120px] px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="uppercase text-[11px] font-bold tracking-[0.2em] text-[#55756c] mb-2">
                {cmsData?.faq_badge ? getLocalizedValue(cmsData.faq_badge) : t.faq.eyebrow}
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-[42px] font-light text-petrol-dark leading-tight">
                {cmsData?.faq_overskrift ? getLocalizedValue(cmsData.faq_overskrift) : t.faq.title}
              </h2>
            </div>
            <div className="lg:col-span-7 border-t-2 border-[#adbbb4]">
              {faqList.map((item, index) => {
                const isOpen = openFaqIndex === index;
                return (
                  <div key={index} className="border-b border-[#c2d5d0]">
                    <button
                      type="button"
                      onClick={() => toggleFaq(index)}
                      aria-expanded={isOpen}
                      className="flex justify-between items-center w-full py-5 text-left font-display text-xl sm:text-2xl font-light text-petrol-dark cursor-pointer group transition-colors"
                    >
                      <span className="pr-4">{item.q}</span>
                      <span className="text-[#2d5a4c] font-sans font-bold text-2xl leading-none select-none">
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>
                    {isOpen && (
                      <div className="pb-5 pr-6 text-[#415e55] text-[15px] leading-relaxed">
                        {item.a?.includes('<') ? (
                          <div dangerouslySetInnerHTML={{ __html: item.a }} />
                        ) : (
                          <p>{item.a}</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Vidensbank & Articles */}
      <section className="py-20 lg:py-24 bg-[#faf9f5] border-t border-[#dce2db]" id="artikler">
        <div className="mx-auto max-w-[1120px] px-6">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
            <div>
              <div className="uppercase text-[11px] font-bold tracking-[0.2em] text-[#82948a] mb-2">
                {cmsData?.artikler_badge ? getLocalizedValue(cmsData.artikler_badge) : t.articles.eyebrow}
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-[42px] font-light text-petrol-dark leading-tight">
                {cmsData?.artikler_overskrift ? getLocalizedValue(cmsData.artikler_overskrift) : t.articles.title}
              </h2>
            </div>
            <Link
              to={localizePath('/vidensbank', lang)}
              className="text-petrol-dark font-semibold text-sm underline underline-offset-4 hover:text-petrol-medium transition-colors"
            >
              {cmsData?.artikler_link_tekst ? getLocalizedValue(cmsData.artikler_link_tekst) : t.articles.linkText}
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 my-8 mb-20">
            {t.articles.items.map((card, idx) => (
              <Link
                key={idx}
                to={localizePath(card.slug, lang)}
                className="group block"
              >
                <div
                  className="h-[210px] rounded-2xl overflow-hidden relative shadow-inner bg-[#e8dfc8]"
                  style={{ backgroundColor: card.bg }}
                >
                  {card.image ? (
                    <img
                      src={card.image}
                      alt={card.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <>
                      <div className="absolute rounded-[48%_52%_60%_40%] opacity-55 filter blur-[1px] w-[75%] h-[80%] bg-[#b4c4a6] top-[5%] left-[4%] rotate-[30deg]" />
                      <div className="absolute rounded-[48%_52%_60%_40%] opacity-55 filter blur-[1px] w-[70%] h-[70%] bg-[#a9c9cc] top-[20%] -right-[12%] -rotate-[30deg]" />
                      <div className="absolute rounded-[48%_52%_60%_40%] opacity-55 filter blur-[1px] w-[60%] h-[55%] bg-[#d7c59c] -bottom-[15%] left-[20%] rotate-[20deg]" />
                    </>
                  )}
                </div>
                <div className="uppercase text-[10px] font-bold tracking-[0.2em] text-[#82948a] mt-4 mb-1">
                  {card.category}
                </div>
                <h3 className="font-display text-xl font-normal text-petrol-dark group-hover:text-petrol-medium transition-colors leading-snug">
                  {card.title}
                </h3>
              </Link>
            ))}
          </div>

          {/* CTA Box 1:1 matching file */}
          <div className="rounded-[25px] bg-[#263f36] text-[#f8f7ed] p-10 sm:p-14 lg:p-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center shadow-md">
            <div className="lg:col-span-7">
              <h2 className="font-display text-3xl sm:text-4xl font-light text-[#f8f7ed] leading-tight mb-3">
                {cmsData?.cta_overskrift ? getLocalizedValue(cmsData.cta_overskrift) : t.ctaSection.title}
              </h2>
              <p className="text-[#b6c6bd] text-[15px] leading-relaxed">
                {cmsData?.cta_beskrivelse ? getLocalizedValue(cmsData.cta_beskrivelse) : t.ctaSection.desc}
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-col items-start lg:items-end">
              <a
                href={cmsData?.hero_cta_link || "https://nordisk-psykiatrisk-klinik.planway.com/"}
                className="inline-block bg-[#d0e3b8] text-[#263f36] rounded-full px-7 py-4 font-bold text-sm sm:text-base whitespace-nowrap hover:bg-white transition-colors duration-300 shadow-sm"
              >
                {cmsData?.cta_knap_tekst ? getLocalizedValue(cmsData.cta_knap_tekst) : t.ctaSection.btn}
              </a>
              <small className="block text-[#b5c4b9] text-xs mt-3">
                {cmsData?.cta_adresse ? getLocalizedValue(cmsData.cta_adresse) : t.ctaSection.address}
              </small>
            </div>
          </div>

          {/* Akut hjælp notice */}
          <div className="border border-[#e1e5dc] rounded-xl px-5 py-3.5 text-xs text-[#6b7d74] mt-5 bg-white/70 flex items-center gap-2">
            {cmsData?.akut_hjaelp_tekst ? (
              <div
                className="[&_strong]:text-[#344c40] [&_p]:m-0"
                dangerouslySetInnerHTML={{ __html: getLocalizedValue(cmsData.akut_hjaelp_tekst) }}
              />
            ) : (
              <div>
                <strong className="text-[#344c40]">{t.notice.label}</strong> {t.notice.text}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
